/**
 * L'ÉTAT D'UNE SURVEILLANCE, EN TOUTES LETTRES : LA TABLE DE PASSAGE.
 * ============================================================================
 *
 * La maquette juge l'état d'une ligne par `etatInfo` (S:5062-5125) sur ses
 * propres valeurs (`declenchee`, `prise_en_compte`, `retour_non_vu`…). Le
 * service sert autre chose, et c'est ce qu'on lit ici :
 *  • la surveillance : `etat` (`armee`, `non_surveillee`, `desactivee`) et
 *    `etat_motif` (`socle/instances.py`, `socle/occurrences.py`) ;
 *  • son alarme ouverte : `etat` (`declenchee_non_prise_en_compte`,
 *    `declenchee_prise_en_compte`, `declenchee_pour_information`,
 *    `retour_a_la_normale_non_vu`), `nature`, `motif_ouverture` (`depassement`
 *    ou `silence_sonde`) et `prise_en_compte`.
 *
 * Deux écarts entre la maquette et le service, tranchés par le code servi :
 *  • la maquette nomme `silence_capteur` le motif d'une sonde muette ; le
 *    service écrit `silence_sonde` (`socle/occurrences.py:373`) ;
 *  • la maquette nomme `ecart_en_attente` l'attente de l'écart inhabituel ; le
 *    service écrit `en_attente_historique` (`socle/occurrences.py:714`). Les
 *    deux codes rendent « En attente ».
 *
 * Les neuf règles s'appliquent dans l'ordre de `etatInfo` ; la première qui
 * s'applique gagne. Une alarme ouverte prime sur l'état de la surveillance :
 * une sonde muette passe la surveillance `non_surveillee` ET ouvre une alarme,
 * que la ligne doit dire. Une alarme ouverte dont l'état n'est reconnu par
 * aucune des règles 1 à 5 ne retombe JAMAIS sur l'état de la surveillance
 * (« En service, rien à signaler » serait un faux calme) : elle se dit « État
 * inconnu » et se range avec ce qui demande l'attention.
 */
import type { Traduire } from "@modules/alarmes-v2/i18n/enregistrer"
import { MESSAGES_ALARMES_V2 } from "@modules/alarmes-v2/i18n/fr"
import type {
    AlarmesV2Alarme,
    AlarmesV2Personne,
    AlarmesV2Surveillance,
} from "@modules/alarmes-v2/types/alarmes-v2"
import {
    formaterDate,
    formaterDuree,
    formaterInstant,
    formaterMesure,
    formaterSeuil,
} from "./mesure"

/** Le code d'état d'une ligne, celui du filtre et du tri de la maquette. */
export type CodeEtatLigne =
    | "declenchee"
    | "prise_en_compte"
    | "retour_non_vu"
    | "armee"
    | "non_surveillee"
    | "desactivee"
    | "inconnu"
    | "alarme_inconnue"

/** Le ton du badge d'état (S:2411-2418). */
export type TonEtat = "danger" | "warn" | "info" | "ok" | "neutre"

/** L'état d'une ligne, prêt à afficher. */
export interface EtatLigne {
    /** La règle de `etatInfo` qui a jugé, de 1 à 9 ; 0 quand aucune ne s'applique (état inconnu). */
    regle: number
    code: CodeEtatLigne
    icone: string
    ton: TonEtat
    mot: string
    detail: string
    second: string
}

/**
 * L'ordre de tri de la maquette (S:5031). Un état de surveillance inconnu,
 * absent de la maquette, se range avec ce qui ne peut pas prévenir : avant les
 * désactivées, puisqu'on ne sait pas qu'elle l'est. Une alarme en cours à
 * l'état inconnu se range en tête, avec les alarmes actives : une alarme est
 * ouverte, on ne sait pas laquelle.
 */
export const ORDRE_DES_ETATS: Record<CodeEtatLigne, number> = {
    declenchee: 0,
    alarme_inconnue: 0,
    prise_en_compte: 1,
    retour_non_vu: 2,
    armee: 3,
    non_surveillee: 4,
    inconnu: 4,
    desactivee: 5,
}

/** Les codes qu'« Actives ou non acquittées » retient (filtre `a_traiter`). */
export const CODES_A_TRAITER: CodeEtatLigne[] = [
    "declenchee",
    "alarme_inconnue",
    "prise_en_compte",
    "retour_non_vu",
]

/** Les motifs d'une mesure absente (S:2409). */
const MOTIFS_MESURE_ABSENTE = [
    "en_attente_premiere_mesure",
    "source_muette",
    "donnee_sans_ecrivain",
]

/** Les motifs d'état que l'écran sait dire (S:2400-2407). */
const MOTIFS_CONNUS = MESSAGES_ALARMES_V2.motifsEtat as Record<string, string>

/** Les motifs de désactivation que l'écran sait dire (S:2392-2398). */
const MOTIFS_DESACTIVATION = MESSAGES_ALARMES_V2.motifsDesactivation as Record<
    string,
    string
>

/** Les deux codes de l'attente de l'écart inhabituel. */
const MOTIFS_EN_ATTENTE = ["ecart_en_attente", "en_attente_historique"]

/** Le nom complet d'une personne servie, ou `null`. */
function nomDe(
    id: string | null | undefined,
    personnes: Record<string, AlarmesV2Personne>
): string | null {
    const p = id ? personnes[id] : undefined
    const nom = [p?.prenom, p?.nom]
        .filter((x) => typeof x === "string" && x !== "")
        .join(" ")
    return nom === "" ? null : nom
}

/** « Rappel 2 sur 4. », « Pas de rappel automatique. », ou rien si non servi. */
function phraseRappel(
    alarme: AlarmesV2Alarme,
    t: Traduire,
    siAucun: boolean
): string {
    const max = alarme.relances?.max
    if (typeof max === "number" && max > 0) {
        return t("alarmesV2.details.rappel", {
            n: alarme.relances?.nb ?? 0,
            max,
        })
    }
    return siAucun && typeof max === "number"
        ? t("alarmesV2.details.pasDeRappel")
        : ""
}

/** Assemble des phrases non vides. */
const phrases = (...p: string[]) => p.filter((x) => x !== "").join(" ")

/** Un libellé de base (S:2411-2418) avec ses deux lignes. */
function etat(
    regle: number,
    code: CodeEtatLigne,
    icone: string,
    ton: TonEtat,
    mot: string,
    detail = "",
    second = ""
): EtatLigne {
    return { regle, code, icone, ton, mot, detail, second }
}

/**
 * Les règles 1 à 5 : une alarme est ouverte. Un état d'alarme qu'aucune règle
 * ne reconnaît (valeur nouvelle, absente, illisible) se dit « État inconnu ».
 */
function etatDeLAlarme(
    alarme: AlarmesV2Alarme,
    t: Traduire,
    personnes: Record<string, AlarmesV2Personne>
): EtatLigne {
    const quand = formaterInstant(alarme.ouverte_le, t) ?? ""
    const seuil = formaterSeuil(alarme.seuils_figes, t)
    const informe =
        alarme.nature === "pour_information" ||
        alarme.etat === "declenchee_pour_information"

    // 1. Une information ne s'acquitte pas et ne rappelle pas (D103, D104).
    if (informe && alarme.etat !== "retour_a_la_normale_non_vu") {
        return etat(
            1,
            "declenchee",
            "◇",
            "info",
            t("alarmesV2.etats.pourInformation"),
            t("alarmesV2.details.infoDepuis", { quand }),
            seuil ? t("alarmesV2.details.seuil", { seuil }) : ""
        )
    }
    if (alarme.etat === "declenchee_non_prise_en_compte") {
        // 2. Une sonde qui ne mesure plus.
        if (alarme.motif_ouverture === "silence_sonde") {
            const duree = formaterDuree(alarme.bilan?.duree_non_observee_s, t)
            return etat(
                2,
                "declenchee",
                "◌",
                "danger",
                t("alarmesV2.etats.defautMesureNonAcquitte"),
                duree
                    ? t("alarmesV2.details.aucuneMesureDepuis", { duree })
                    : t("alarmesV2.details.aucuneMesure"),
                phrases(
                    t("alarmesV2.details.activeDepuisNonAcquittee", { quand }),
                    phraseRappel(alarme, t, false)
                )
            )
        }
        // 3. Active, personne ne l'a acquittée.
        return etat(
            3,
            "declenchee",
            "▲",
            "danger",
            t("alarmesV2.etats.activeNonAcquittee"),
            t("alarmesV2.details.activeDepuisPersonne", { quand }),
            phrases(
                seuil
                    ? t("alarmesV2.details.seuilAuDeclenchement", { seuil })
                    : "",
                phraseRappel(alarme, t, true)
            )
        )
    }
    // 4. Active, acquittée.
    if (alarme.etat === "declenchee_prise_en_compte") {
        const nom = nomDe(alarme.prise_en_compte?.par, personnes)
        const le = formaterInstant(alarme.prise_en_compte?.le, t) ?? ""
        return etat(
            4,
            "prise_en_compte",
            "△",
            "warn",
            t("alarmesV2.etats.activeAcquittee"),
            nom
                ? t("alarmesV2.details.acquitteePar", { nom, quand: le })
                : t("alarmesV2.details.acquittee", { quand: le }),
            seuil
                ? t("alarmesV2.details.activeDepuisSeuil", { quand, seuil })
                : t("alarmesV2.details.activeDepuis", { quand })
        )
    }
    // 5. Revenue à la normale, personne ne l'a acquittée.
    if (alarme.etat === "retour_a_la_normale_non_vu") {
        const bilan = alarme.bilan
        const extreme = bilan?.valeur_extreme
        const duree = formaterDuree(bilan?.duree_hors_seuil_s, t)
        const second =
            typeof extreme === "number" && duree
                ? t("alarmesV2.details.auPlus", {
                      valeur: formaterMesure(
                          extreme,
                          alarme.seuils_figes?.unite,
                          t
                      ),
                      quand: formaterInstant(bilan?.valeur_extreme_le, t) ?? "",
                      duree,
                  })
                : ""
        return etat(
            5,
            "retour_non_vu",
            "◐",
            "info",
            t("alarmesV2.etats.revenueNonAcquittee"),
            t("alarmesV2.details.revenueNonAcquittee", {
                quand: formaterInstant(alarme.retour_le, t) ?? "",
            }),
            second
        )
    }
    return etat(
        0,
        "alarme_inconnue",
        "?",
        "warn",
        t("alarmesV2.etats.inconnu"),
        t("alarmesV2.details.etatInconnuAlarme")
    )
}

/** Les règles 6 à 9 : aucune alarme ouverte, l'état de la surveillance. */
function etatDeLaSurveillance(
    s: AlarmesV2Surveillance,
    t: Traduire
): EtatLigne {
    const motif = s.etat_motif ?? ""
    if (s.etat === "armee") {
        const date = formaterDate(s.etat_depuis)
        return etat(
            6,
            "armee",
            "●",
            "ok",
            t("alarmesV2.etats.enService"),
            date
                ? t("alarmesV2.details.normaleDepuis", { date })
                : t("alarmesV2.details.normale")
        )
    }
    if (s.etat === "non_surveillee") {
        if (MOTIFS_EN_ATTENTE.includes(motif)) {
            return etat(
                7,
                "non_surveillee",
                "◌",
                "neutre",
                t("alarmesV2.etats.enAttente"),
                t("alarmesV2.details.enAttente"),
                t("alarmesV2.details.enAttenteSuite")
            )
        }
        const texte = MOTIFS_CONNUS[motif]
        if (MOTIFS_MESURE_ABSENTE.includes(motif)) {
            return etat(
                8,
                "non_surveillee",
                "◌",
                "neutre",
                t("alarmesV2.etats.defautMesure"),
                t("alarmesV2.details.aucuneMesureRecue", {
                    motif: t(`alarmesV2.motifsEtat.${motif}`),
                })
            )
        }
        return etat(
            8,
            "non_surveillee",
            "◌",
            "neutre",
            t("alarmesV2.etats.nePeutPasPrevenir"),
            texte
                ? t("alarmesV2.details.nePeutPasPrevenir", {
                      motif: t(`alarmesV2.motifsEtat.${motif}`),
                  })
                : t("alarmesV2.details.nePeutPasPrevenirSeul")
        )
    }
    // Ni en service, ni non surveillée, ni désactivée (valeur nouvelle, absente,
    // illisible) : on ne sait pas, et on le dit. Jamais « Désactivée » par défaut.
    if (s.etat !== "desactivee") {
        return etat(
            0,
            "inconnu",
            "?",
            "neutre",
            t("alarmesV2.etats.inconnu"),
            t("alarmesV2.details.etatInconnu")
        )
    }
    // 9. Désactivée, avec la date, le motif et le texte du client.
    const date = formaterDate(s.etat_depuis)
    const cleMotif = s.desactivation_motif ?? ""
    const libelle =
        cleMotif in MOTIFS_DESACTIVATION
            ? t(`alarmesV2.motifsDesactivation.${cleMotif}`)
            : ""
    const detail =
        (date
            ? t("alarmesV2.details.desactiveeLe", { date })
            : t("alarmesV2.details.desactiveeSeul")) +
        (libelle ? t("alarmesV2.details.motifSuffixe", { motif: libelle }) : "")
    return etat(
        9,
        "desactivee",
        "○",
        "neutre",
        t("alarmesV2.etats.desactivee"),
        detail,
        s.desactivation_texte ?? ""
    )
}

/**
 * L'état d'une ligne de la liste.
 * @param s - la surveillance
 * @param alarme - son alarme ouverte, ou `null`
 * @param t - la traduction
 * @param personnes - les noms servis avec les alarmes
 */
export function etatDeLaLigne(
    s: AlarmesV2Surveillance,
    alarme: AlarmesV2Alarme | null,
    t: Traduire,
    personnes: Record<string, AlarmesV2Personne> = {}
): EtatLigne {
    const parAlarme =
        alarme && alarme.etat !== "close"
            ? etatDeLAlarme(alarme, t, personnes)
            : null
    return parAlarme ?? etatDeLaSurveillance(s, t)
}

/**
 * Vrai pour une surveillance « pour information », repliée sous son titre.
 * La nature absente (documents écrits avant D103) se lit « à traiter ».
 */
export function estPourInformation(s: AlarmesV2Surveillance): boolean {
    return s.nature === "pour_information"
}
